import { ArrowDown, ArrowUp, ArrowUpDown, Ban, CheckCircle, ExternalLink, Mail, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Common/button';
import { Badge } from '@/components/Common/badge';
import { cn } from '@/lib/utils';
import type { AdminUserRow, Organization, UserRole } from '@/types';
import type { AdminUserSort, AdminUserSortField, SortOrder } from '@/types/adminUsers';
import type { Profile } from '@/types/profiles';
import { UserRoles, USER_ROLES } from '@/types';

export interface AdminUsersTableProps {
  rows: AdminUserRow[];
  organizations: Organization[];
  loading?: boolean;
  assigningUserId?: string | null;
  /** Show column: created_at */
  showCreated?: boolean;
  /** Show column: logto_status */
  showLogtoStatus?: boolean;
  /** Show column: organization (holds the org selector when onAssignOrga is set) */
  showOrganization?: boolean;
  /** Show column: role (holds the role selector when onRoleChange is set) */
  showRole?: boolean;
  /** Show column: profile (feature-level RBAC) */
  showProfile?: boolean;
  /** Available profiles for the profile selector */
  profiles?: Profile[];
  onAssignProfile?: (userId: string, profileId: string | null) => void;
  /** Logto dashboard base URL (e.g. https://logto.lafabriq.ai) for "Show in Logto" link; if not set, button hidden */
  logtoDashboardUrl?: string | null;
  onAssignOrga?: (userId: string, organizationId: string | null) => void;
  onResendMagicLink?: (row: AdminUserRow) => void;
  onDelete?: (row: AdminUserRow) => void;
  onAddToDb?: (row: AdminUserRow, organizationId?: string | null) => void;
  onDeleteFromLogto?: (row: AdminUserRow) => void;
  onInvite?: (row: AdminUserRow) => void;
  onToggleActive?: (row: AdminUserRow, isActive: boolean) => void;
  /** Super Admin only: change any user's role (super_admin, admin, user) */
  onRoleChange?: (row: AdminUserRow, newRole: UserRole) => void;
  /** Current server-side sort, highlighted in the header */
  sort?: AdminUserSort;
  /** When set, sortable headers become buttons; otherwise headers are plain text */
  onSortChange?: (field: AdminUserSortField) => void;
  emptyMessage?: string;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

interface SortableThProps {
  field: AdminUserSortField;
  label: string;
  sort?: AdminUserSort;
  onSortChange?: (field: AdminUserSortField) => void;
}

const SortableTh: React.FC<SortableThProps> = ({ field, label, sort, onSortChange }) => {
  const { t } = useTranslation();

  if (!onSortChange) {
    return <th className="text-left py-2 px-2">{label}</th>;
  }

  const activeOrder: SortOrder | null = sort && sort.field === field ? sort.order : null;
  const ariaSort = activeOrder === null ? 'none' : activeOrder === 'asc' ? 'ascending' : 'descending';
  const Icon = activeOrder === null ? ArrowUpDown : activeOrder === 'asc' ? ArrowUp : ArrowDown;

  return (
    <th className="text-left py-2 px-2" aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSortChange(field)}
        className="-mx-1 inline-flex items-center gap-1 rounded-sm px-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        {label}
        <span className="sr-only">, {t('admin.users.sort.sortBy', 'Sort by {{column}}', { column: label })}</span>
        <Icon
          aria-hidden="true"
          className={cn('h-3.5 w-3.5 flex-shrink-0', activeOrder === null ? 'text-muted-foreground/60' : 'text-foreground')}
        />
      </button>
    </th>
  );
};

export const AdminUsersTable: React.FC<AdminUsersTableProps> = ({
  rows,
  organizations,
  loading = false,
  assigningUserId = null,
  showCreated = true,
  showLogtoStatus = true,
  showOrganization = true,
  showRole = false,
  showProfile = false,
  profiles = [],
  onAssignProfile,
  logtoDashboardUrl = null,
  onAssignOrga,
  onResendMagicLink,
  onDelete,
  onAddToDb,
  onDeleteFromLogto,
  onInvite,
  onToggleActive,
  onRoleChange,
  sort,
  onSortChange,
  emptyMessage,
}) => {
  const { t } = useTranslation();
  const [addToDbOrg, setAddToDbOrg] = React.useState<Record<string, string>>({});

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-6">
        {emptyMessage ?? t('admin.users.noUsers', 'No users')}
      </p>
    );
  }

  const roleLabels: Record<UserRole, string> = {
    [UserRoles.SuperAdmin]: t('admin.users.roleSuperAdmin', 'Super Admin'),
    [UserRoles.Admin]: t('admin.users.roleAdmin', 'Admin'),
    [UserRoles.User]: t('admin.users.roleUser', 'User'),
    [UserRoles.Demo]: t('admin.users.roleDemo', 'Demo'),
  };
  const sortProps = { sort, onSortChange };
  const isDbRow = (row: AdminUserRow) => row.source === 'db' || row.source === 'db_and_logto';

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <SortableTh field="email" label={t('admin.users.table.email', 'Email')} {...sortProps} />
            <SortableTh field="name" label={t('admin.users.table.name', 'Name')} {...sortProps} />
            {showOrganization && (
              <SortableTh field="organization" label={t('admin.users.table.organization', 'Organization')} {...sortProps} />
            )}
            {showRole && (
              <SortableTh field="role" label={t('admin.users.table.role', 'Role')} {...sortProps} />
            )}
            {showProfile && (
              <SortableTh field="profile" label={t('admin.users.table.profile', 'Profile')} {...sortProps} />
            )}
            {showCreated && (
              <SortableTh field="created_at" label={t('admin.users.table.created', 'Created')} {...sortProps} />
            )}
            {showLogtoStatus && (
              <th className="text-left py-2 px-2">{t('admin.users.table.logtoStatus', 'Logto')}</th>
            )}
            <th className="text-right py-2 px-2">{t('admin.users.table.actions', 'Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.source === 'logto' ? `logto-${row.id}` : row.id} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 px-2">{row.email}</td>
              <td className="py-2 px-2">
                <span className="flex items-center gap-1.5">
                  {row.name ?? '—'}
                  {row.role === UserRoles.SuperAdmin && (
                    <span title={t('admin.users.superAdminBadge', 'Super Admin')}>
                      <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0" />
                    </span>
                  )}
                </span>
              </td>
              {showOrganization && (
                <td className="py-2 px-2">
                  {isDbRow(row) && onAssignOrga ? (
                    <select
                      className="rounded border border-input bg-background px-2 py-1 text-xs max-w-[180px]"
                      value={row.organization_id ?? ''}
                      onChange={(e) => onAssignOrga(row.id, e.target.value || null)}
                      disabled={assigningUserId !== null && assigningUserId !== row.id}
                      aria-label={t('admin.users.table.organization', 'Organization')}
                      title={t('admin.users.table.organization', 'Organization')}
                    >
                      <option value="">— {t('admin.users.noOrga', 'No org')} —</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    row.organization_name ?? '—'
                  )}
                </td>
              )}
              {showRole && (
                <td className="py-2 px-2">
                  {isDbRow(row) && onRoleChange ? (
                    <select
                      className="rounded border border-input bg-background px-2 py-1 text-xs max-w-[140px]"
                      value={row.role && USER_ROLES.includes(row.role as UserRole) ? row.role : UserRoles.User}
                      onChange={(e) => onRoleChange(row, e.target.value as UserRole)}
                      aria-label={t('admin.users.role', 'Role')}
                      title={t('admin.users.role', 'Role')}
                    >
                      <option value={UserRoles.User}>{roleLabels[UserRoles.User]}</option>
                      <option value={UserRoles.Admin}>{roleLabels[UserRoles.Admin]}</option>
                      <option value={UserRoles.SuperAdmin}>{roleLabels[UserRoles.SuperAdmin]}</option>
                      {/* Demo is not offered as a target role; listed only so demo rows show their real role. */}
                      {row.role === UserRoles.Demo && (
                        <option value={UserRoles.Demo}>{roleLabels[UserRoles.Demo]}</option>
                      )}
                    </select>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      {row.role && USER_ROLES.includes(row.role as UserRole) ? roleLabels[row.role as UserRole] : (row.role ?? '—')}
                    </span>
                  )}
                </td>
              )}
              {showProfile && (
                <td className="py-2 px-2">
                  {isDbRow(row) && onAssignProfile ? (
                    <select
                      className="rounded border border-input bg-background px-2 py-1 text-xs max-w-[160px]"
                      value={row.profile_id ?? ''}
                      onChange={(e) => onAssignProfile(row.id, e.target.value || null)}
                      title={t('admin.users.profile', 'Profile')}
                    >
                      <option value="">
                        — {t('admin.users.profileDefault', 'Default (role)')} —
                      </option>
                      {profiles.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      {row.profile_name ?? '—'}
                    </span>
                  )}
                </td>
              )}
              {showCreated && (
                <td className="py-2 px-2 text-muted-foreground">{formatDate(row.created_at)}</td>
              )}
              {showLogtoStatus && (
                <td className="py-2 px-2">
                  {row.logto_status != null ? (
                    <Badge variant={row.logto_status === 'active' ? 'default' : 'secondary'}>
                      {row.logto_status === 'active'
                        ? t('admin.users.statusActive', 'Active')
                        : t('admin.users.statusNotInLogto', 'Not in Logto')}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </td>
              )}
              <td className="py-2 px-2">
                <div className="flex flex-wrap justify-end gap-1">
                  {row.source === 'db_and_logto' && onResendMagicLink && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onResendMagicLink(row)}
                      title={t('admin.users.resendMagicLink', 'Resend magic link')}
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                  )}
                  {row.source === 'db' && onInvite && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onInvite(row)}
                      title={t('admin.users.inviteUser', 'Invite / Send Logto link')}
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                  )}
                  {row.source === 'logto' && onAddToDb && (
                    <>
                      <select
                        className="rounded border border-input bg-background px-2 py-1 text-xs max-w-[140px]"
                        value={addToDbOrg[row.id] ?? ''}
                        onChange={(e) => setAddToDbOrg((s) => ({ ...s, [row.id]: e.target.value }))}
                      >
                        <option value="">— {t('admin.users.noOrga', 'No org')} —</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAddToDb(row, addToDbOrg[row.id] || null)}
                        title={t('admin.users.addToDb', 'Add to DB')}
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {logtoDashboardUrl && (row.source === 'db_and_logto' || row.logto_id) && (
                    <a
                      href={`${logtoDashboardUrl.replace(/\/$/, '')}/console/users/${row.logto_id ?? row.id}/settings`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex"
                      title={t('admin.users.showInLogto', 'Show in Logto')}
                    >
                      <Button variant="ghost" size="sm" type="button">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </a>
                  )}
                  {isDbRow(row) && onToggleActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={row.is_active === false ? 'text-green-600 hover:text-green-700' : 'text-orange-500 hover:text-orange-600'}
                      onClick={() => onToggleActive(row, !(row.is_active !== false))}
                      title={row.is_active === false ? t('admin.users.activateUser', 'Activate user') : t('admin.users.deactivateUser', 'Deactivate user')}
                    >
                      {row.is_active === false
                        ? <CheckCircle className="h-4 w-4" />
                        : <Ban className="h-4 w-4" />}
                    </Button>
                  )}
                  {isDbRow(row) && onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => onDelete(row)}
                      title={t('global.delete')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  {row.source === 'logto' && onDeleteFromLogto && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => onDeleteFromLogto(row)}
                      title={t('admin.users.deleteFromLogto', 'Delete from Logto')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
